// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file KYC.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Gestão de Verificação KYC
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  VerifiedUser,
  Person,
  Description,
  CheckCircle,
  Cancel,
  Pending,
  Refresh,
  Visibility,
  ThumbUp,
  ThumbDown,
  Info,
  Image,
  Badge,
  Home,
  AttachMoney,
} from '@mui/icons-material';
import DataTable, { Column, StatusChip, DateCell } from '../../components/Common/DataTable';
import { useTranslation } from 'react-i18next';
import bankingService, { KycVerification, KycDocument, KycFilters } from '../../services/banking.service';

const KYC_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  pending: { label: 'Pendente', color: 'warning' },
  in_review: { label: 'Em Análise', color: 'info' },
  approved: { label: 'Aprovado', color: 'success' },
  rejected: { label: 'Rejeitado', color: 'error' },
  requires_info: { label: 'Info Necessária', color: 'warning' },
};

const DOC_STATUS: Record<string, { label: string; color: 'success' | 'warning' | 'error' }> = {
  pending: { label: 'Pendente', color: 'warning' },
  verified: { label: 'Verificado', color: 'success' },
  rejected: { label: 'Rejeitado', color: 'error' },
};

const DOC_TYPES: Record<string, { label: string; icon: React.ReactNode }> = {
  id_front: { label: 'BI - Frente', icon: <Badge /> },
  id_back: { label: 'BI - Verso', icon: <Badge /> },
  passport: { label: 'Passaporte', icon: <Description /> },
  selfie: { label: 'Selfie', icon: <Person /> },
  proof_of_address: { label: 'Comprovativo de Morada', icon: <Home /> },
  proof_of_income: { label: 'Comprovativo de Rendimento', icon: <AttachMoney /> },
};

const KYC_LEVELS: Record<string, string> = {
  basic: 'Básico',
  standard: 'Standard',
  premium: 'Premium',
  business: 'Empresarial',
};

const BankingKYC: React.FC = () => {
  const { t } = useTranslation();
  const [verifications, setVerifications] = useState<KycVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');

  // Dialog states
  const [selectedKyc, setSelectedKyc] = useState<KycVerification | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [requestInfoOpen, setRequestInfoOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inReview: 0,
    approved: 0,
    rejected: 0,
  });

  const fetchVerifications = useCallback(async () => {
    setLoading(true);
    try {
      const filters: KycFilters = {
        page: page + 1,
        limit: rowsPerPage,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(levelFilter !== 'all' && { requestedLevel: levelFilter }),
      };

      const response = await bankingService.getKycVerifications(filters);
      setVerifications(response.data);
      setTotalCount(response.pagination?.total || 0);

      if (response.summary) {
        setStats({
          total: response.summary.total,
          pending: response.summary.pending,
          inReview: response.summary.inReview,
          approved: response.summary.approved,
          rejected: response.summary.rejected,
        });
      }
    } catch (err: any) {
      setError(err.message || t('banking.kyc.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, levelFilter]);

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  const handleApprove = async () => {
    if (!selectedKyc) return;
    setActionLoading(true);
    try {
      await bankingService.approveKyc(selectedKyc.id, notes);
      setApproveOpen(false);
      setNotes('');
      fetchVerifications();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedKyc || !rejectReason) return;
    setActionLoading(true);
    try {
      await bankingService.rejectKyc(selectedKyc.id, rejectReason);
      setRejectOpen(false);
      setRejectReason('');
      fetchVerifications();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!selectedKyc || !infoMessage) return;
    setActionLoading(true);
    try {
      await bankingService.requestKycInfo(selectedKyc.id, infoMessage);
      setRequestInfoOpen(false);
      setInfoMessage('');
      fetchVerifications();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyDocument = async (docId: string) => {
    if (!selectedKyc) return;
    try {
      await bankingService.verifyDocument(selectedKyc.id, docId);
      // Refresh the selected KYC
      const updated = await bankingService.getKycVerification(selectedKyc.id);
      setSelectedKyc(updated);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRejectDocument = async (docId: string, reason: string) => {
    if (!selectedKyc) return;
    try {
      await bankingService.rejectDocument(selectedKyc.id, docId, reason);
      const updated = await bankingService.getKycVerification(selectedKyc.id);
      setSelectedKyc(updated);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const columns: Column<KycVerification>[] = [
    { id: 'id', label: 'ID', format: (_, row) => row.id.slice(0, 8) + '...' },
    {
      id: 'customer',
      label: 'Cliente',
      format: (_, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 32, height: 32 }}><Person /></Avatar>
          <Box>
            <Typography variant="body2">{row.customerName}</Typography>
            <Typography variant="caption" color="text.secondary">{row.customerPhone}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'levels',
      label: 'Nível',
      format: (_, row) => (
        <Box>
          <Chip size="small" label={KYC_LEVELS[row.currentLevel]} variant="outlined" />
          <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>→</Typography>
          <Chip size="small" label={KYC_LEVELS[row.requestedLevel]} color="primary" />
        </Box>
      ),
    },
    {
      id: 'documents',
      label: 'Documentos',
      format: (_, row) => {
        const verified = row.documents.filter(d => d.status === 'verified').length;
        const total = row.documents.length;
        return (
          <Typography variant="body2">
            {verified}/{total} verificados
          </Typography>
        );
      },
    },
    {
      id: 'status',
      label: 'Estado',
      format: (_, row) => {
        const status = KYC_STATUS[row.status];
        return <Chip label={status?.label || row.status} size="small" color={status?.color || 'default'} />;
      },
    },
    { id: 'submittedAt', label: 'Data', format: (_, row) => <DateCell value={row.submittedAt} /> },
    {
      id: 'actions',
      label: 'Ações',
      align: 'center',
      format: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Ver detalhes">
            <IconButton size="small" onClick={() => { setSelectedKyc(row); setDetailsOpen(true); }}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          {(row.status === 'pending' || row.status === 'in_review') && (
            <>
              <Tooltip title="Aprovar">
                <IconButton size="small" color="success" onClick={() => { setSelectedKyc(row); setApproveOpen(true); }}>
                  <ThumbUp fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Rejeitar">
                <IconButton size="small" color="error" onClick={() => { setSelectedKyc(row); setRejectOpen(true); }}>
                  <ThumbDown fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Pedir Informação">
                <IconButton size="small" color="info" onClick={() => { setSelectedKyc(row); setRequestInfoOpen(true); }}>
                  <Info fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          {t('banking.kyc.title')}
        </Typography>
        <Button startIcon={<Refresh />} variant="outlined" onClick={fetchVerifications}>
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="text.secondary">Total</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Pending color="warning" />
              <Typography color="text.secondary">Pendentes</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.pending}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Visibility color="info" />
              <Typography color="text.secondary">Em Análise</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.inReview}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle color="success" />
              <Typography color="text.secondary">Aprovados</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.approved}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 2.4 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Cancel color="error" />
              <Typography color="text.secondary">Rejeitados</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.rejected}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Estado"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="in_review">Em Análise</MenuItem>
                <MenuItem value="approved">Aprovado</MenuItem>
                <MenuItem value="rejected">Rejeitado</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Nível Solicitado"
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <MenuItem value="all">Todos</MenuItem>
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="business">Empresarial</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Data Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <DataTable
          columns={columns}
          data={verifications}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
        />
      )}

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('banking.kyc.details')}</DialogTitle>
        <DialogContent>
          {selectedKyc && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption">Cliente</Typography>
                  <Typography>{selectedKyc.customerName}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption">Telefone</Typography>
                  <Typography>{selectedKyc.customerPhone}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption">Nível Atual</Typography>
                  <Chip label={KYC_LEVELS[selectedKyc.currentLevel]} size="small" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption">Nível Solicitado</Typography>
                  <Chip label={KYC_LEVELS[selectedKyc.requestedLevel]} size="small" color="primary" />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Documentos
              </Typography>
              <List>
                {selectedKyc.documents.map((doc) => {
                  const docType = DOC_TYPES[doc.type];
                  const docStatus = DOC_STATUS[doc.status];
                  return (
                    <ListItem
                      key={doc.id}
                      secondaryAction={
                        doc.status === 'pending' ? (
                          <Box>
                            <IconButton color="success" onClick={() => handleVerifyDocument(doc.id)}>
                              <CheckCircle />
                            </IconButton>
                            <IconButton color="error" onClick={() => handleRejectDocument(doc.id, 'Documento inválido')}>
                              <Cancel />
                            </IconButton>
                          </Box>
                        ) : (
                          <Chip label={docStatus?.label} color={docStatus?.color} size="small" />
                        )
                      }
                    >
                      <ListItemAvatar>
                        <Avatar>{docType?.icon || <Image />}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={docType?.label || doc.type}
                        secondary={doc.fileName}
                      />
                    </ListItem>
                  );
                })}
              </List>

              {selectedKyc.rejectionReason && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Motivo de rejeição: {selectedKyc.rejectionReason}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailsOpen(false)}>Fechar</Button></DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}>
        <DialogTitle>{t('banking.kyc.approveTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Confirma a aprovação do nível {selectedKyc && KYC_LEVELS[selectedKyc.requestedLevel]} para {selectedKyc?.customerName}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Observações (opcional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)} disabled={actionLoading}>Cancelar</Button>
          <Button variant="contained" color="success" onClick={handleApprove} disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Aprovar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <DialogTitle>{t('banking.kyc.rejectTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Motivo da Rejeição"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={actionLoading}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={actionLoading || !rejectReason}>
            {actionLoading ? <CircularProgress size={20} /> : 'Rejeitar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={requestInfoOpen} onClose={() => setRequestInfoOpen(false)}>
        <DialogTitle>{t('banking.kyc.requestInfoTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Mensagem para o cliente"
            placeholder="Descreva a informação ou documento adicional necessário..."
            value={infoMessage}
            onChange={(e) => setInfoMessage(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestInfoOpen(false)} disabled={actionLoading}>Cancelar</Button>
          <Button variant="contained" onClick={handleRequestInfo} disabled={actionLoading || !infoMessage}>
            {actionLoading ? <CircularProgress size={20} /> : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BankingKYC;
